import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];

  let filteredComics = activeCategory === 'all'
    ? comics
    : comics.filter((c) => c.category === activeCategory);

  if (searchTerm.trim()) {
    filteredComics = filteredComics.filter(
      (c) =>
        c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.tags.some((t) => t.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }

  filteredComics = [...filteredComics].sort((a, b) => {
    if (sortBy === 'popular') return b.views - a.views;
    if (sortBy === 'rating') return b.rating - a.rating;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name}韩漫 - 韩漫网站`,
    description: `浏览${currentCategory.name}类韩国漫画，海量免费${currentCategory.name}韩漫在线阅读，下拉式浏览，每日更新。`,
    url: typeof window !== 'undefined' ? window.location.href : '',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫网站',
      url: typeof window !== 'undefined' ? window.location.origin : '',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin + '/' : '/' },
        { '@type': 'ListItem', position: 2, name: `${currentCategory.name}韩漫` },
      ],
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-50 border-b border-background-200">
          <div className="px-4 md:px-6 max-w-6xl mx-auto py-4">
            <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs w-4 h-4 flex items-center justify-center"></i>
              <span className="text-foreground-800 font-medium">{currentCategory.name}韩漫</span>
            </nav>
          </div>
        </div>

        {/* Category Header */}
        <section className="bg-background-100 py-8 md:py-12">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
              {currentCategory.name}韩漫
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
              {activeCategory === 'all'
                ? '浏览全部韩漫作品，涵盖恋爱、奇幻、动作、悬疑等丰富题材。免费韩漫在线阅读、下拉式浏览，每日更新热门韩国漫画。'
                : `浏览${currentCategory.name}类韩国漫画，海量免费${currentCategory.name}韩漫在线阅读。下拉式浏览，每日更新，为你推荐最精彩的${currentCategory.name}韩漫作品。`}
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="bg-background-50 border-b border-background-200 sticky top-16 z-30">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-primary-500 hover:bg-primary-50'
                  }`}
                >
                  <i className={`${cat.icon} text-sm w-4 h-4 flex items-center justify-center`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Toolbar */}
        <section className="bg-background-50 border-b border-background-100">
          <div className="px-4 md:px-6 max-w-6xl mx-auto py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-sm text-foreground-500">
              共 <strong className="text-foreground-800">{filteredComics.length}</strong> 部作品
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="搜索作品或作者..."
                  className="w-40 md:w-56 pl-8 pr-4 py-1.5 text-sm bg-background-100 border border-background-200 rounded-full text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                />
                <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-400 text-xs w-3 h-3 flex items-center justify-center"></i>
              </div>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                {[
                  { key: 'latest', label: '最新' },
                  { key: 'popular', label: '人气' },
                  { key: 'rating', label: '评分' },
                ].map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setSortBy(s.key as typeof sortBy)}
                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                      sortBy === s.key
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-primary-500'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Comic Grid */}
        <section className="py-8 md:py-12 bg-background-50">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} {...comic} variant="default" />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-search-line text-2xl text-foreground-400 w-8 h-8 flex items-center justify-center"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-800 mb-2">未找到相关作品</h3>
                <p className="text-sm text-foreground-500">试试其他关键词或分类吧</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}